import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Button, Alert, ProgressBar, Icon } from '../../../../core/ui';
import apiClient, { getModelInfo } from '../../../../core/api/client';
import { useTranslation } from '../../../../core/context/TranslationContext';
import styles from './UploadReceipt.module.css';

// 422 codes for an LLM provider failure; the image itself was fine.
const PROVIDER_FAILURE_CODES = ['LLM_RATE_LIMITED', 'LLM_UNAVAILABLE', 'LLM_AUTH'];
const RETRYABLE_CODES = ['LLM_RATE_LIMITED', 'LLM_UNAVAILABLE'];

const UploadReceipt = () => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [modelInfo, setModelInfo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchModelInfo = async () => {
      try {
        const response = await getModelInfo();
        if (response.status === 200) {
          setModelInfo(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch model info:', error);
      }
    };

    fetchModelInfo();
  }, []);

  const acceptFile = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith('image/')) {
      setError({ text: 'Please select an image file', retryable: false });
      return;
    }
    setFile(selectedFile);
    setError(null);
    setSuccess(false);

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleFileChange = (e) => acceptFile(e.target.files[0]);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!loading) setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!loading) acceptFile(e.dataTransfer.files[0]);
  };

  const failureFor = (err) => {
    if (PROVIDER_FAILURE_CODES.includes(err?.code)) {
      return { text: t(`receipts.uploadError.${err.code}`), retryable: RETRYABLE_CODES.includes(err.code) };
    }

    let errorMessage = t('uploadError');
    if (err) {
      switch (err.code) {
        case 'FILE_TOO_LARGE':
          errorMessage = t('errorFileTooLarge', { maxSize: '10MB' });
          break;
        case 'UNSUPPORTED_FILE_TYPE':
          errorMessage = t('errorUnsupportedFormat');
          break;
        case 'DUPLICATE_RECEIPT':
          errorMessage = t('errorDuplicateReceipt');
          break;
        case 'RECEIPT_INVALID_FORMAT':
          errorMessage = t('errorInvalidFormat');
          break;
        case 'PROCESSING_ERROR':
          errorMessage = t('errorProcessing');
          break;
        default:
          errorMessage = err.message || t('uploadError');
      }
      errorMessage += ' ' + err.message;
    }
    return { text: errorMessage, retryable: false };
  };

  const upload = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post('/api/receipts/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.status === 200 && response.data) {
        setSuccess(true);
        setFile(null);
        setPreview(null);
        setUploadProgress(0);
        if (inputRef.current) inputRef.current.value = '';
      } else if (response.data) {
        throw response.data;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setSuccess(false);
      setError(failureFor(err));
      console.error('Upload error:', {
        code: err?.code,
        message: err?.message,
        timestamp: err?.timestamp
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    upload();
  };

  const dropzoneClass = [
    styles.dropzone,
    dragging ? styles.dropzoneActive : '',
    file ? styles.dropzoneHasFile : '',
    loading ? styles.dropzoneDisabled : '',
  ].filter(Boolean).join(' ');

  return (
    <Container className={styles.uploadContainer}>
      <Card>
        <Card.Header>
          <Card.Title>{t('uploadReceipt')}</Card.Title>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert
              variant="danger"
              dismissible
              onDismiss={() => setError(null)}
              className={styles.alert}
            >
              <div>
                <strong>{t('errorHeading')}</strong>
                <p className={styles.errorText}>{error.text}</p>
                {error.text === t('errorProcessing') && (
                  <p className={styles.errorHelp}>{t('errorProcessingHelp')}</p>
                )}
                {error.retryable && file && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={upload}
                    disabled={loading}
                    className={styles.retryButton}
                  >
                    {t('receipts.retryUpload')}
                  </Button>
                )}
              </div>
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onDismiss={() => setSuccess(false)}>
              {t('uploadSuccess')}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label}>{t('selectReceiptImage')}</label>

              <div
                className={dropzoneClass}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={loading}
                  className={styles.fileInputHidden}
                  tabIndex={-1}
                />

                <div className={styles.dropzoneIcon}>
                  <Icon name={file ? 'fa-file-image' : 'fa-cloud-upload-alt'} />
                </div>

                {file ? (
                  <span className={styles.dropzoneFileName}>{file.name}</span>
                ) : (
                  <p className={styles.dropzoneText}>
                    <strong>Choose a file</strong> or drag it here
                  </p>
                )}
              </div>

              <p className={styles.helpText}>{t('supportedFormats')}</p>
            </div>

            {preview && (
              <div className={styles.previewContainer}>
                <img
                  src={preview}
                  alt="Receipt preview"
                  className={styles.receiptImage}
                />
              </div>
            )}

            {loading && uploadProgress > 0 && (
              <div className={styles.progressWrapper}>
                <ProgressBar
                  now={uploadProgress}
                  label={`${uploadProgress}%`}
                  variant="primary"
                />
              </div>
            )}

            <div className={styles.buttonRow}>
              <Button
                type="submit"
                disabled={!file || loading}
                className={styles.submitBtn}
              >
                {loading ? (
                  <>
                    <span className={styles.spinner} />
                    {t('processing')}
                  </>
                ) : (
                  t('uploadReceipt')
                )}
              </Button>
              {modelInfo && (
                <div className={styles.infoIcon} title={modelInfo.displayText}>
                  <Icon name="fa-info-circle" size="lg" />
                </div>
              )}
            </div>
          </form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default UploadReceipt;
